/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { jsx } from '@compiled/react';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';

import { ProactiveDecorations } from '../../../pm-plugins/ai-proactive/decorations';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import { ModalRegion } from '../ModalRegion/ModalRegion';
import { ModalRegionErrorBoundary } from '../ModalRegionErrorBoundary/ModalRegionErrorBoundary';
import { PreviewScreenWithLogic } from '../PreviewScreenWithLogic/PreviewScreenWithLogic';

export const ContentComponent = () => {
	const { editorView, api, providerFactory } = useContentComponentContext();

	usePluginStateEffect(api, ['emoji', 'mention'], ({ emojiState, mentionState }) => {
		// If the api updates at all update the provider factory
		!!emojiState?.emojiProvider &&
			providerFactory.setProvider('emojiProvider', Promise.resolve(emojiState.emojiProvider));
		!!mentionState?.mentionProvider &&
			providerFactory.setProvider('mentionProvider', Promise.resolve(mentionState.mentionProvider));
	});

	const endExperience = useCallback(() => {
		editorView.focus();
	}, [editorView]);

	const selectedRecommendationId = useSharedPluginStateSelector(
		api,
		'aiProactive.selectedRecommendationId',
	);
	const decorationSet = useSharedPluginStateSelector(api, 'aiProactive.decorationSet');

	const decoration = decorationSet
		?.find(
			undefined,
			undefined,
			(spec) => spec.key === ProactiveDecorations.RECOMMENDATION_SELECTED,
		)
		?.shift();

	if (!decoration || !decorationSet) {
		return null;
	}

	const modalDecorationElements = editorView?.dom.querySelectorAll<HTMLElement>(
		'.ai-proactive-recommendation-selected',
	);

	// If there's more then 1 selected node, which can happen when the cursor is in the middle of a paragrpah
	// the decorations are split around the cursor. In this situation we should just use the decorations first
	// parent.
	const modalDecorationElement: HTMLElement | null =
		modalDecorationElements.length > 1
			? // Ignored via go/ees005
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				(modalDecorationElements?.[modalDecorationElements.length - 1]
					?.parentElement as HTMLElement)
			: modalDecorationElements?.[0];

	// This protects against cases where there is an active decoration
	// but the document has not been updated to include it
	if (!modalDecorationElement) {
		return null;
	}

	if (!selectedRecommendationId) {
		return null;
	}

	return (
		<ModalRegionErrorBoundary onCloseFallback={endExperience}>
			<ModalRegion
				key={selectedRecommendationId}
				decoration={decoration}
				modalDecorationElement={modalDecorationElement}
				endExperience={endExperience}
				autoScroll
			>
				<PreviewScreenWithLogic />
			</ModalRegion>
		</ModalRegionErrorBoundary>
	);
};
